<script>
  import katexify from "../katexify";
  import { selectAll } from "d3-selection";
  import { scaleOrdinal } from "d3-scale";
  import Scatterplot from "./Scatterplot.svelte";
  import { scatterData } from "../datasets.js";
  import { onMount } from "svelte";
  import { Temperature, DecisionBoundary, Prediction } from "../data-store.js";

  let scatterClass;

  const seriesNames = new Set();

  const target2event = {
    0: () => {
      scatterClass.hidePoints();
      scatterClass.hideCurve();
      scatterClass.hideBoundary();
      scatterClass.hideExample();
    },
    1: () => {
      // points appear
      scatterClass.showPoints();
      scatterClass.hideCurve();
      scatterClass.hideBoundary();
      scatterClass.hideExample();
    },

    2: () => {
      // curve appears
      scatterClass.showCurve();
      scatterClass.showPoints();
      scatterClass.hideBoundary();
      scatterClass.hideExample();
    },
    3: () => {
      // decision boundary appears
      scatterClass.showCurve();
      scatterClass.showPoints();
      scatterClass.showBoundary();
    },

    4: () => {
      scatterClass.showCurve();
      scatterClass.showPoints();
      scatterClass.showBoundary();
      scatterClass.showExample();
    },
  };

  function fireEvent(entryIndex) {
    if (entryIndex in target2event) {
      target2event[entryIndex]();
    }
  }

  onMount(() => {
    // store elements to track
    let sections = selectAll(".step").nodes();

    // observe elements to track
    sections.forEach((section) => {
      observer.observe(section);
    });
  });

  // options for intersection observer
  const options = {
    DecisionBoundary: { $DecisionBoundary },
  };

  let observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      // check if visible or not
      if (entry.isIntersecting) {
        // resolve stage in graph
        const entryIndex = entry.target.getAttribute("data-index");
        const node = document.querySelectorAll(`[data-index="${entryIndex}"]`);

        if (entryIndex in target2event) {
          fireEvent(entryIndex);
        }
      }
    });
  }, options);
</script>

<h2 class="body-header">How It Works</h2>
<p class="body-text">
  Let’s bring this to life with an example. Imagine you want to go for a hike in
  Seattle. You need to guess if it will be sunny or rainy so you can decide
  whether to hit the trails or stay inside with a coffee. You know Seattle rains
  a lot, but the summers are often nice. The big question is: can we predict
  the weather just by looking at the temperature?
</p>
<section>
  <div class="section-container">
    <div class="steps-container">
      <div class="step" data-index="1">
        <div class="step-content">
          <p>
            First, let's look at our two possible outcomes: Rainy Days and Sunny
            Days. Computers prefer numbers, so let's label a Rainy Day as 0 and
            a Sunny Day as 1. We have one main clue to help us decide: the
            temperature in degrees Fahrenheit. We can plot each past day on a
            graph to see how temperature relates to the weather.
          </p>
        </div>
      </div>
      <div class="step" data-index="2">
        <div class="step-content">
          <p>
            We can't just draw a straight line through this data. A straight
            line goes on forever in both directions, but our answer needs to
            stay between 0 (Rain) and 1 (Sun). A straight line might give us a
            nonsensical answer like "1.5" or "-0.2."
            <br /><br />

            Instead, we use a "Logistic Function," which looks like an S-curve.
            This curve stays neatly between 0 and 1. We can think of the value
            on this curve as a percentage—specifically, the probability that a
            specific temperature will result in a sunny day.
          </p>
        </div>
      </div>
      <div class="step" data-index="3">
        <div class="step-content">
          <p>
            Now that we have a probability (like 0.8 or 80%), we need to make a
            final "Yes or No" prediction. We do this using a "Decision Boundary"
            or a cutoff point. Usually, this is set at 0.5. If the probability
            is higher than 0.5, we predict Sun; if it's lower, we predict Rain.
            You can adjust this cutoff based on your preference. For example, if
            you hate getting wet, you might raise the boundary to be extra
            careful, only predicting a sunny day when the probability is very
            high.
          </p>
        </div>
      </div>
      <div class="step" data-index="4">
        <div class="step-content">
          <p>
            Give it a try! Drag the sliders below to see how the temperature and
            your chosen cutoff point change the prediction.
          </p>
          <br /><br />
          <div id="input-container">
            <p>
              <span class="bold">Temperature: </span>
              {$Temperature} Degrees Fahrenheit
            </p>
            <input
              type="range"
              min="20"
              max="100"
              bind:value={$Temperature}
              class="slider"
              id="tempSlider"
            />
          </div>
          <div id="input-container">
            <p>
              <span class="bold">Decision Boundary: </span>{$DecisionBoundary}
            </p>
            <input
              type="range"
              min="0"
              max="1"
              step="0.01"
              bind:value={$DecisionBoundary}
              class="slider"
              id="boundarySlider"
            />
          </div>
          <br /><br />
          <p>
            The prediction is a <span class="bold">{$Prediction}</span>.
          </p>
        </div>
      </div>
      <div class="spacer" />
    </div>
    <div class="charts-container">
      <div class="chart-one">
        <Scatterplot bind:this={scatterClass} />
      </div>
    </div>
  </div>

  <br /><br />
</section>

<style>
  .body-header {
    margin-top: 2em;
  }

  .chart-one {
    width: 100%;
    height: 100%;
  }
  /* space after scroll is finished */
  .spacer {
    height: 40vh;
  }

  .charts-container {
    position: sticky;
    top: 20%;
    width: 50%;
    height: 60vh;
    margin-right: 5%;
  }

  .section-container {
    margin-top: 1em;
    text-align: center;
    transition: background 100ms;
    display: flex;
  }

  .step {
    height: 100vh;
    display: flex;
    place-items: center;
    justify-content: center;
    background: transparent;
  }

  .step-content {
    font-size: 18px;
    border-radius: 1px;
    background: #ffff;
    padding: 0.5rem 1rem;
    display: flex;
    flex-direction: column;
    justify-content: center;
    transition: background 500ms ease;
    text-align: left;
    width: 75%;
    margin: auto;
    max-width: 500px;
    font-family: var(--font-main);
    line-height: 1.3;
    border: 5px solid var(--default);
    opacity: 0.9;
  }

  .steps-container {
    height: 100%;
  }

  .steps-container {
    flex: 1 1 40%;
    z-index: 10;
  }

  .slider {
    -webkit-appearance: none;
    width: 100%;
    height: 15px;
    border-radius: 5px;
    background: var(--stone);
    outline: none;
    opacity: 0.9;
    -webkit-transition: 0.2s;
    transition: opacity 0.2s;
    border-color: var(--squidink);
  }

  .slider::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 25px;
    height: 25px;
    border-radius: 50%;
    background: var(--sky);
    cursor: pointer;
  }

  /* Comment out the following line to always make it 'text-on-top' */
  @media screen and (max-width: 950px) {
    .section-container {
      flex-direction: column-reverse;
    }

    .steps-container {
    }

    .charts-container {
      top: 7.5%;
      width: 95%;
      margin: auto;
    }

    .step {
      height: 120vh;
      background: transparent;
      color: var(--squidink);
    }

    .step-content {
      width: 95%;
      max-width: 768px;
      font-size: 17px;
      line-height: 1.6;
    }
  }
</style>
